/**
 * DocumentDetailModal Component
 *
 * Modal for viewing document details and downloading.
 * Features:
 * - Fetches document detail with presigned download URL
 * - Shows document metadata (title, type, size, created date)
 * - PDF preview using PdfViewer component
 * - Image preview
 * - Handles URL expiration gracefully
 */

import React, { useCallback, useEffect, useState } from 'react';
import { PdfViewer } from '@components/PdfViewer';
import {
  Close as CloseIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { useLazyGetDocumentQuery } from '../store/api';

import { DOCUMENT_TYPE_OPTIONS } from './DocumentFilters';

// Format file size to human readable
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

// Format date to localized string with time
const formatDateTime = (dateString: string): string => {
  return new Date(dateString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// Get document type label
const getDocTypeLabel = (type: string | null | undefined): string => {
  if (!type) return 'Unknown';
  const option = DOCUMENT_TYPE_OPTIONS.find(opt => opt.value === type);
  return option?.label ?? type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

// Check if URL has expired
const isUrlExpired = (expiresAt: number | null | undefined): boolean => {
  if (!expiresAt) return false;
  // Add 5 minute buffer before actual expiration
  return Date.now() / 1000 > expiresAt - 300;
};

interface DocumentDetailModalProps {
  open: boolean;
  documentId: string | null;
  onClose: () => void;
}

export const DocumentDetailModal: React.FC<DocumentDetailModalProps> = ({
  open,
  documentId,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  const [fetchDocument, { data: document, isLoading, isFetching, error }] =
    useLazyGetDocumentQuery();

  const [urlExpired, setUrlExpired] = useState(false);

  // Fetch document when modal opens
  useEffect(() => {
    if (open && documentId) {
      setUrlExpired(false);
      void fetchDocument({ documentId });
    }
  }, [open, documentId, fetchDocument]);

  // Check for URL expiration periodically
  useEffect(() => {
    if (!document?.download_url_expires_at) return;

    const checkExpiration = () => {
      if (isUrlExpired(document.download_url_expires_at)) {
        setUrlExpired(true);
      }
    };

    // Check immediately
    checkExpiration();

    // Check every minute
    const interval = setInterval(checkExpiration, 60000);

    return () => {
      clearInterval(interval);
    };
  }, [document?.download_url_expires_at]);

  // Handle refresh to get new download URL
  const handleRefresh = useCallback(() => {
    if (documentId) {
      setUrlExpired(false);
      void fetchDocument({ documentId });
    }
  }, [documentId, fetchDocument]);

  // Handle download for non-PDF files
  const handleDownload = useCallback(() => {
    if (document?.download_url && !urlExpired) {
      window.open(document.download_url, '_blank');
    }
  }, [document?.download_url, urlExpired]);

  const isPdf = document?.content_type === 'application/pdf';
  const isImage = document?.content_type?.startsWith('image/');
  const canPreview = document && (isPdf || isImage) && !urlExpired;
  const hasDownloadUrl = document?.download_url && !urlExpired;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 2,
          height: fullScreen ? '100%' : '90vh',
          maxHeight: '90vh',
        },
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
        <DocumentIcon color="primary" />
        <Typography variant="h6" component="span" sx={{ flex: 1, fontWeight: 600 }} noWrap>
          {document?.document_title || document?.filename || 'Document Details'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isLoading || isFetching ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 300,
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert severity="error">
              Failed to load document details. Please try again.
            </Alert>
          </Box>
        ) : document ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            {/* URL Expiration Warning */}
            {urlExpired && (
              <Box sx={{ p: 2, bgcolor: 'warning.light', flexShrink: 0 }}>
                <Alert
                  severity="warning"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                    >
                      Refresh
                    </Button>
                  }
                >
                  Download link has expired. Click refresh to get a new link.
                </Alert>
              </Box>
            )}

            {/* Document Metadata - Compact Header */}
            <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', flexShrink: 0 }}>
              <Stack
                direction="row"
                spacing={3}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
              >
                <Chip
                  label={getDocTypeLabel(document.document_type)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
                <Typography variant="body2" color="text.secondary">
                  {formatFileSize(document.size_bytes)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatDateTime(document.created_at)}
                </Typography>
              </Stack>
            </Box>

            <Divider />

            {/* Preview Area */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 3, bgcolor: 'grey.100' }}>
              {canPreview && document.download_url ? (
                isPdf ? (
                  <PdfViewer
                    url={document.download_url}
                    filename={document.filename}
                    height="100%"
                    eventMetadata={{
                      documentType: document.document_type || undefined,
                      patientId: document.patient_id || undefined,
                      documentId: document.document_id,
                    }}
                  />
                ) : isImage ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '100%',
                    }}
                  >
                    <Box
                      component="img"
                      src={document.download_url}
                      alt={document.document_title || document.filename}
                      sx={{
                        maxWidth: '100%',
                        maxHeight: '100%',
                        objectFit: 'contain',
                        borderRadius: 1,
                        boxShadow: 2,
                      }}
                    />
                  </Box>
                ) : null
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    py: 6,
                  }}
                >
                  <DocumentIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary" gutterBottom>
                    {urlExpired
                      ? 'Link expired. Please refresh to view.'
                      : 'Preview not available for this file type'}
                  </Typography>
                  {hasDownloadUrl && !isPdf && (
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownload}
                      sx={{ mt: 2 }}
                    >
                      Download File
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default DocumentDetailModal;
